/**
 * Text alerts — know the moment a customer texts.
 *
 *   phone notifications  Web Push: "Turn on text alerts" once per phone (iPhone: from the home-screen
 *                        app, iOS 16.4+). n8n "[C] Twilio 1 - Incoming Texts" → Netlify push-notify →
 *                        every phone that turned them on. Tapping one opens that customer's thread.
 *   in-app banner+sound  while the app is open: a banner with the text + a short chime (the chime is
 *                        skipped on phones with notifications on — the phone already plays its own).
 * Who gets them: every phone that turned them on (everyone shares the admin login for now).
 */
import { useSyncExternalStore } from 'react';
import { getSupabase } from './auth';
import { CURRENT_USER } from './currentUser';
import { LIVE } from './communications';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

export const PUSH_SUPPORTED =
  typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
const isIOS = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/.test(navigator.userAgent);
const isStandalone = () =>
  typeof window !== 'undefined' && (window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches);

/* ---------- the service worker (only for notifications) ---------- */
export const registerServiceWorker = () => {
  if (!LIVE || !('serviceWorker' in navigator) || process.env.NODE_ENV !== 'production') return;
  navigator.serviceWorker.register('/sw.js').catch((err) => console.warn('Text alerts unavailable:', err.message));
};

/* ---------- this phone's alert state ---------- */
// unsupported | home-screen (iPhone in Safari: add to home screen first) | off | on | blocked
let state = 'off';
const listeners = new Set();
const set = (s) => {
  state = s;
  listeners.forEach((fn) => fn());
};

const b64ToBytes = (b64) => {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

const currentSubscription = async () => {
  const reg = await navigator.serviceWorker.getRegistration('/');
  return reg ? reg.pushManager.getSubscription() : null;
};

export const refreshAlertState = async () => {
  if (!LIVE || !VAPID_PUBLIC_KEY) return set('unsupported');
  if (!PUSH_SUPPORTED) return set(isIOS && !isStandalone() ? 'home-screen' : 'unsupported');
  if (Notification.permission === 'denied') return set('blocked');
  try {
    const sub = await currentSubscription();
    set(sub && Notification.permission === 'granted' ? 'on' : 'off');
  } catch {
    set('off');
  }
  return undefined;
};

// must be called from a tap (iPhone only asks for permission from a user gesture)
export const enableTextAlerts = async () => {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    await refreshAlertState();
    throw new Error(permission === 'denied' ? 'Notifications are blocked for this app in the phone’s settings' : 'Notifications weren’t allowed');
  }
  const reg = await navigator.serviceWorker.ready;
  const sub =
    (await reg.pushManager.getSubscription()) ||
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToBytes(VAPID_PUBLIC_KEY) }));
  const supabase = getSupabase();
  const { error } = await supabase.rpc('push_subscribe', { p_sub: sub.toJSON(), p_name: CURRENT_USER.name || null });
  if (error) throw new Error(error.message || 'Couldn’t turn on alerts');
  set('on');
};

export const disableTextAlerts = async () => {
  const sub = await currentSubscription();
  if (sub) {
    const supabase = getSupabase();
    await supabase.rpc('push_unsubscribe', { p_endpoint: sub.endpoint });
    await sub.unsubscribe();
  }
  set('off');
};

export const useTextAlertState = () =>
  useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => state
  );

/* ---------- the in-app chime ---------- */
let audio = null;
// phones only allow sound after the user has touched the page — unlock on the first tap
export const unlockChime = () => {
  if (audio || typeof window === 'undefined') return;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return;
  audio = new Ctx();
  if (audio.state === 'suspended') audio.resume().catch(() => {});
};

export const playChime = () => {
  if (!audio || state === 'on') return; // the phone's own notification sound covers it
  const t = audio.currentTime;
  [880, 1320].forEach((f, i) => {
    const o = audio.createOscillator();
    const g = audio.createGain();
    o.frequency.value = f;
    o.type = 'sine';
    g.gain.setValueAtTime(0.0001, t + i * 0.14);
    g.gain.exponentialRampToValueAtTime(0.25, t + i * 0.14 + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + i * 0.14 + 0.22);
    o.connect(g).connect(audio.destination);
    o.start(t + i * 0.14);
    o.stop(t + i * 0.14 + 0.25);
  });
};

/* ---------- new customer texts while the app is open ---------- */
// every 15 s: texts / emails that came in since the last look → onNew([{ contact, name, body, channel }])
export const watchIncoming = (onNew) => {
  if (!LIVE) return () => {};
  let since = new Date().toISOString();
  let stopped = false;
  const tick = async () => {
    const supabase = getSupabase();
    if (stopped || !supabase || document.visibilityState !== 'visible') return;
    const { data, error } = await supabase
      .from('comms_messages')
      .select('id, contact, channel, body, subject, customer_name, at')
      .eq('direction', 'in')
      .in('channel', ['sms', 'email'])
      .gt('at', since)
      .order('at', { ascending: true })
      .limit(20);
    if (error || !data || !data.length) return;
    since = data[data.length - 1].at;
    onNew(data.map((m) => ({ contact: m.contact, name: m.customer_name || '', body: m.channel === 'email' ? m.subject || m.body : m.body, channel: m.channel })));
  };
  const timer = setInterval(tick, 15000);
  return () => {
    stopped = true;
    clearInterval(timer);
  };
};
